import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, Subscription, EMPTY, of, throwError } from 'rxjs';
import { filter, map, tap, take, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { MessageService } from 'primeng/api';


import { RecipientPersonalization } from '../models/personalization.model';
import {
    PersonalizationSearchResponseApiModel,
    DistributionRecipientQuery,
    PersonalizationSummaryApiModel,
    PersonalizationSummary,
    PersonalizationValue
} from '../../../api/distributions/model/distribution-personalization.api.model';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { SortFieldEnum } from '@Common/TargetingShared/enum/SortField.enum';
import { BaseSearchService, TargetingFacetCollection } from './base-search.service';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { TargetingResultFactory } from '../factories/targeting-result.factory';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { FilterTypeModel } from '@Common/Filter/models/abstract-filter-type.model';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { TargetingSearch } from '../models/targeting-search.model';
import { PageCriteriaModelInterface } from '@Common/Misc/models/page-criteria.model.interface';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { TargetingSearchFacetsEnum } from '@api/targeting/enum/targeting-search-facets.enum';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { RichTextDisplayElementModel } from '@Common/PageBuilder/models/rich-text-display-element.model';
import { DistributionBlockTypeEnum } from '../enums/distribution-block-type.enum';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { OwnershipType } from '@api/targeting/enum/ownership.type.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { PersonalizationServiceInterface } from '@Common/Dialogs/services/personalization-service.interface';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { DistributionModel } from '../models/distribution.model';


/**
 * Responsible for managing personalization data for all recipients of a specific distribution.
 * That includes filtering, sorting, saving, etc.
 */
@Injectable()
export class PersonalizationService extends BaseSearchService implements PersonalizationServiceInterface {
    public static SIZE_ERROR_RESPONSE = 'cannot be larger';

    private static COMMON_PERSONALIZATION_SIZE = 255;
    private static INTRO_OUTRO_PERSONALIZATION_SIZE = 3000;

    distributionId: number;
    selectedPersonalizationTag: PersonalizationTagEnum = PersonalizationTagEnum.FNAME;

    private personalizationTags: PersonalizationTagEnum[] = [];
    private personalizationTags$ = new BehaviorSubject<PersonalizationTagEnum[]>(this.personalizationTags);

    private recipientPersonalizations: RecipientPersonalization[] = [];
    private recipientPersonalizations$ = new BehaviorSubject<RecipientPersonalization[]>(this.recipientPersonalizations);

    private personalizationSummary: PersonalizationSummary[] = [];
    private personalizationSummary$ = new BehaviorSubject<PersonalizationSummary[]>(this.personalizationSummary);

    protected subscriptions: Subscription[] = [];

    readonly sortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_CIRCULATION,
            displayString: 'Discover.targeting.sortFields.circulation'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_VIEWERS,
            displayString: 'Discover.targeting.sortFields.viewers'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_LISTENERS,
            displayString: 'Discover.targeting.sortFields.listeners'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_FOLLOWERS,
            displayString: 'Discover.targeting.sortFields.followers'
        },
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        }
    ];

    readonly contactOnlyTags = [
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        // NEP-18499 PersonalizationTagEnum.NAME,
    ];

    readonly personalizationOrder = [
        // NEP-18499 PersonalizationTagEnum.NAME,
        PersonalizationTagEnum.PREFIX,
        PersonalizationTagEnum.FNAME,
        PersonalizationTagEnum.LNAME,
        PersonalizationTagEnum.OUTLET_NAME,
        PersonalizationTagEnum.X_HANDLE,
        PersonalizationTagEnum.INTRO,
        PersonalizationTagEnum.OUTRO
    ];

    readonly personalizationSizeMap = new Map<PersonalizationTagEnum, number>([
        // All other personalizations are 255
        [PersonalizationTagEnum.INTRO, PersonalizationService.INTRO_OUTRO_PERSONALIZATION_SIZE],
        [PersonalizationTagEnum.OUTRO, PersonalizationService.INTRO_OUTRO_PERSONALIZATION_SIZE],
    ]);

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected discoverRoutingService: DiscoverRoutingService,
        protected userPreferencesService: UserPreferencesService,
        protected distributionService: DistributionService,
        protected distributionApi: DistributionApi,
        protected messageService: MessageService
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApi
        );
        this.setFilters(this.allFilters);
        this.targetingSortFields$.next(this.sortFields);
        this.sortField = this.sortFields[0];
        this.sortField$.next(this.sortField);
        this.setPageCriteria({page: 1, pageSize: 25}, false);

        this.sortOrder = SortOrderEnum.ascending;
        this.sortOrder$.next(this.sortOrder);

        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userPreference => userPreference.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings)
            ).subscribe(userPreference => {
                this.pageCriteria.pageSize = userPreference.pageSize;
                this.setPageCriteria(this.pageCriteria, false);
            }),
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x),
                distinctUntilChanged(),
                switchMap(distribution => {
                    this.distributionId = distribution.id;
                    this.personalizationTags = this.parsePersonalizations(distribution)
                        .sort((a, b) => this.personalizationOrder.indexOf(a) - this.personalizationOrder.indexOf(b));

                    this.personalizationTags$.next(this.personalizationTags);

                    if (!this.distributionId) {
                        return EMPTY;
                    }

                    return this.distributionApi.getPersonalizationSummary(this.distributionId, false);
                })
            ).subscribe(
                (personalizationSummary: PersonalizationSummaryApiModel) => {
                    const summary = personalizationSummary.details.filter(x => this.personalizationTags.includes(x.tagType));
                    this.updateSummary(summary);
                }
            )
        );
    }


    getFilterDefinitions(): FilterTypeModel[] {
        return [
            this.contactNameFilter,
            this.outletNameFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.locationFilter,
            this.subjectFilter,
            this.languageFilter,
            this.targetAudienceRegionFilter,
            this.coverageFocusFilter,
            this.audienceReachFilter,
            this.marketSectorFilter,
            this.mediaTypeFilter,
            this.productTypeFilter,
            this.productFrequencyFilter,
            this.targetAudienceFilter,
            this.listFilter,
            this.statusFilter
        ];
    }


    override setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true) {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.doSearch();
            }
        }
    }

    doSearch() {
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        this.searchLoading$.next(true);
        this.search(this.selectedPersonalizationTag, filters).pipe(take(1)).subscribe({
            next: (collection) => {
                this.setPrimaryFacets(collection);
                this.fetchListMemberships();
                this.searchLoading$.next(false);
            },
            error: (err) => {
                console.log(err);
                this.searchLoading$.next(false);
            }
        });
    }

    private search(tagType: PersonalizationTagEnum, filters: TargetingSearch): Observable<TargetingFacetCollection> {
        const rows = this.pageCriteria.pageSize;
        const start = this.pageCriteria.page > 0 ? (this.pageCriteria.page - 1) * rows : 0;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : PersonalizationTagEnum.PREFIX;

        let search: DistributionRecipientQuery;

        if (filters) {
            search = {
                tagType: tagType,
                ownershipTypes: [OwnershipType.PRIVATE, OwnershipType.SYSTEM],
                facets: Object.values(TargetingSearchFacetsEnum),
                ...filters.toApiModel(true, filters).query
            };
        } else {
            search = {
                tagType: tagType,
                ownershipTypes: [OwnershipType.PRIVATE, OwnershipType.SYSTEM],
                facets: Object.values(TargetingSearchFacetsEnum),
            };
        }

        if (this.contactOnlyTags.includes(tagType)) {
            search.searchMode = EntityType.CONTACT;
        }


        const searchResponse: Observable<PersonalizationSearchResponseApiModel> = this.distributionApi.personalizationSearch(
            this.distributionId,
            search,
            start,
            rows,
            sortOrder,
            sortField
        );

        return searchResponse.pipe(
            tap(response => {
                this.nResults = response.numResults;
                this.nResults$.next(this.nResults);
                this.searchLoading$.next(false);

                this.recipientPersonalizations = response.results.map(x => new RecipientPersonalization(x));
                this.recipientPersonalizations$.next(this.recipientPersonalizations);
            }),
            map(response => this.parseFacets(response.facetResults))
        );
    }

    getRecipientPersonalizations(): Observable<RecipientPersonalization[]> {
        return this.recipientPersonalizations$;
    }

    getPersonalizationTags(): Observable<PersonalizationTagEnum[]> {
        return this.personalizationTags$;
    }

    getSummary(): Observable<PersonalizationSummary[]> {
        return this.personalizationSummary$;
    }

    updateSummary(personalizationSummary: PersonalizationSummary[]) {
        this.personalizationSummary = personalizationSummary;
        this.personalizationSummary$.next(personalizationSummary);
    }


    getFilterListItems() {

    }

    getUnsavedSearchSummary(includeSavedSearch: boolean) {
        this.distributionApi.getPersonalizationSummary(this.distributionId, includeSavedSearch).subscribe(
            (personalizationSummary: PersonalizationSummaryApiModel) => {
                const summary = personalizationSummary.details.filter(x => this.personalizationTags.includes(x.tagType));
                this.updateSummary(summary);
            }
        );
    }

    handleUpsertError(
        error: HttpErrorResponse, personalization: PersonalizationValue, nameLabel: string
    ): Observable<any> {
        let detail = this.localizationService.transform(
            'Discover.distribution.personalizationSaved.error.body',
            { entity: nameLabel }
        );
        if (
            error?.status ===  400
            && typeof error?.error === 'string'
            && error.error.includes(PersonalizationService.SIZE_ERROR_RESPONSE)
        ) {
            detail = this.localizationService.transform(
                'Discover.distribution.personalizationSaved.error.size.body',
                {
                    tag: personalization.tagType,
                    size: this.getSizeLimitForPersonalization(personalization)
                }

            );
        }
        this.messageService.add({
            severity: messageSeverityEnum.error,
            summary: 'Discover.distribution.personalizationSaved.error.heading',
            detail: detail
        });
        return throwError(() => error);
    }

    getSizeLimitForPersonalization(personalization: PersonalizationValue): number {
        if (this.personalizationSizeMap.has(personalization.tagType)) {
            return this.personalizationSizeMap.get(personalization.tagType);
        }
        return PersonalizationService.COMMON_PERSONALIZATION_SIZE;
    }

    /**
     * Parse through block content of type DistributionBlockTypeEnum.text and return all
     * personalizations tags that are present.
     */
    private parsePersonalizations(distribution: DistributionModel): PersonalizationTagEnum[] {
        const personalizationMap = new Map<PersonalizationTagEnum, boolean>([
            // NEP-18499 [PersonalizationTagEnum.NAME, false],
            [PersonalizationTagEnum.PREFIX, false],
            [PersonalizationTagEnum.FNAME, false],
            [PersonalizationTagEnum.LNAME, false],
            [PersonalizationTagEnum.OUTLET_NAME, false],
            [PersonalizationTagEnum.X_HANDLE, false],
            [PersonalizationTagEnum.INTRO, false],
            [PersonalizationTagEnum.OUTRO, false],
        ]);

        const textBlocks = [];
        distribution.blocks.forEach(block => {
            switch (block.type.key) {
                case DistributionBlockTypeEnum.text:
                    textBlocks.push(block);
                    break;
                case DistributionBlockTypeEnum.oneColumnLayout:
                case DistributionBlockTypeEnum.twoColumnLayout:
                case DistributionBlockTypeEnum.threeColumnLayout:
                    (block as LayoutBlock).blocks.forEach(column => {
                        column.forEach(x => {
                            if (x.type.key === DistributionBlockTypeEnum.text) {
                                textBlocks.push(x);
                            }
                        });
                    });
            }
        });
        textBlocks.forEach(block => {

            /** NEP-18499
                if (!personalizationMap.get(PersonalizationTagEnum.NAME)) {
                    personalizationMap.set(
                        PersonalizationTagEnum.NAME,
                        (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.NAME}%]`)
                    );
                }
            */

            if (!personalizationMap.get(PersonalizationTagEnum.PREFIX)) {
                personalizationMap.set(
                    PersonalizationTagEnum.PREFIX,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.PREFIX}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.FNAME)) {
                personalizationMap.set(
                    PersonalizationTagEnum.FNAME,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.FNAME}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.LNAME)) {
                personalizationMap.set(
                    PersonalizationTagEnum.LNAME,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.LNAME}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.OUTLET_NAME)) {
                personalizationMap.set(
                    PersonalizationTagEnum.OUTLET_NAME,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.OUTLET_NAME}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.X_HANDLE)) {
                personalizationMap.set(
                    PersonalizationTagEnum.X_HANDLE,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.X_HANDLE}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.INTRO)) {
                personalizationMap.set(
                    PersonalizationTagEnum.INTRO,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.INTRO}%]`)
                );
            }

            if (!personalizationMap.get(PersonalizationTagEnum.OUTRO)) {
                personalizationMap.set(
                    PersonalizationTagEnum.OUTRO,
                    (block.displayElement as RichTextDisplayElementModel).text?.includes(`[%${PersonalizationTagEnum.OUTRO}%]`)
                );
            }
        });

        for(const tag of personalizationMap.keys()) {
            if (distribution.subjectLine?.includes(`[%${tag}%]`)) {
                personalizationMap.set(tag, true);
            }
            if (distribution.previewText?.includes(`[%${tag}%]`)) {
                personalizationMap.set(tag, true);
            }
        }

        return Array.from(personalizationMap, ([key, value]) => (value) ? key : null).filter(x => !!x);
    }
}
